import { forwardRef, useState, useEffect, useMemo } from 'react';
import type { PropsWithoutRef, Ref } from 'react';
import styled, { css } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import createDOMPurify from 'dompurify';

import { StyledPopover } from '../Popover';
import { defaultThemeProp } from '../../theme';
import { calculateFontSize } from '../../styles';
import type { FontSize } from '../../styles';
import type {
  BaseProps,
  ForwardRefForwardPropsComponent,
  NoChildrenProp,
  TestIdProp
} from '../../types';
import { allowedURI, withTestIds } from '../../utils';
import { useTestIds } from '../../hooks';

import { getHTMLTestIds } from './HTML.test-ids';

const DOMPurify = createDOMPurify();

export interface HTMLProps extends BaseProps, NoChildrenProp, TestIdProp {
  content: string;
  customTags?: string[];
  ref?: Ref<HTMLDivElement>;
}

export const listStyles = css`
  ul,
  ol {
    padding-inline-start: 2.5rem;
  }

  li {
    margin: 0.5rem 0;
  }

  ul li {
    ul {
      margin-inline-start: 1rem;
      list-style-type: circle;
    }

    ul ul {
      margin-inline-start: 2rem;
      list-style-type: square;
    }

    ul ul ul {
      margin-inline-start: 3rem;
      list-style-type: disc;
    }
  }

  ol li {
    ol {
      margin-inline-start: 1rem;
      list-style-type: lower-alpha;
    }

    ol ol {
      margin-inline-start: 2rem;
      list-style-type: lower-roman;
    }

    ol ol ol {
      margin-inline-start: 3rem;
      list-style-type: decimal;
    }
  }
`;

export const getTableStyles = (theme: DefaultTheme) => css`
  border-collapse: collapse;

  td,
  th {
    border: 0.0625rem solid ${theme.base.palette['border-line']};
    min-width: 6.25rem;
    padding: ${theme.base.spacing};
  }

  tr:first-child {
    td:first-child,
    th:first-child {
      border-top-left-radius: 0.125rem;
    }

    td:last-child,
    th:last-child {
      border-top-right-radius: 0.125rem;
    }
  }

  tr:last-child {
    td:first-child,
    th:first-child {
      border-bottom-left-radius: 0.125rem;
    }

    td:last-child,
    th:last-child {
      border-bottom-right-radius: 0.125rem;
    }
  }
`;

export const getHtmlStyles = (theme: DefaultTheme) => {
  const {
    base: { 'font-size': baseFontSize, 'font-scale': baseFontScale, palette },
    components: { text }
  } = theme;
  const fontSize = calculateFontSize(baseFontSize, baseFontScale);
  return `
    ${listStyles}
    table {
      ${getTableStyles(theme)}
    }
    img {
      max-width: 100%;
      height: auto;
      border-radius: 0.25rem;
    }
    a {
      color: ${palette.interactive};
    }
    pre {
      white-space: break-spaces;
    }
    p {
      min-height: ${fontSize[text.primary['font-size'] as FontSize]};
    }
    h1 {
      font-size: ${fontSize[text.h1['font-size'] as FontSize]};
      font-weight: ${text.h1['font-weight']};
      min-height: ${fontSize[text.h1['font-size'] as FontSize]};
    }
    h2 {
      font-size: ${fontSize[text.h2['font-size'] as FontSize]};
      font-weight: ${text.h2['font-weight']};
      min-height: ${fontSize[text.h2['font-size'] as FontSize]};
    }
    h3 {
      font-size: ${fontSize[text.h3['font-size'] as FontSize]};
      font-weight: ${text.h3['font-weight']};
      min-height: ${fontSize[text.h3['font-size'] as FontSize]};
    }
    h4 {
      font-size: ${fontSize[text.h4['font-size'] as FontSize]};
      font-weight: ${text.h4['font-weight']};
      min-height: ${fontSize[text.h4['font-size'] as FontSize]};
    }
    h5 {
      font-size: ${fontSize[text.h5['font-size'] as FontSize]};
      font-weight: ${text.h5['font-weight']};
      min-height: ${fontSize[text.h5['font-size'] as FontSize]};
    }
    h6 {
      font-size: ${fontSize[text.h6['font-size'] as FontSize]};
      font-weight: ${text.h6['font-weight']};
      min-height: ${fontSize[text.h6['font-size'] as FontSize]};
    }
  `;
};

export const StyledHTML = styled.div(({ theme }) => {
  return css`
    width: 100%;
    overflow-x: auto;
    overflow-wrap: break-word;
    contain: paint;
    ${getHtmlStyles(theme)}

    ${StyledPopover} ul {
      ul {
        padding-inline-start: 0;
      }
      ol {
        padding-inline-start: 0;
      }
    }

    ${StyledPopover} li {
      margin: 0;
    }
  `;
});

StyledHTML.defaultProps = defaultThemeProp;

if (DOMPurify.isSupported) {
  const targetBlankSet = new WeakSet<HTMLAnchorElement>();

  DOMPurify.addHook('beforeSanitizeAttributes', node => {
    // Can be passed text nodes
    if (!(node instanceof HTMLElement || node instanceof SVGElement)) return;

    node.removeAttribute('class');

    if (
      node.tagName === 'A' &&
      node.hasAttribute('href') &&
      node.getAttribute('target') === '_blank'
    ) {
      targetBlankSet.add(node as HTMLAnchorElement);
    }
  });

  DOMPurify.addHook('afterSanitizeAttributes', node => {
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      const href = node.getAttribute('href')!;

      let fullURL: URL;
      try {
        fullURL = new URL(href, window.location.href);
      } catch (e) {
        return;
      }

      if (window.location.origin !== fullURL.origin) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener');
      } else if (targetBlankSet.has(node as HTMLAnchorElement)) {
        node.setAttribute('target', '_blank');
        targetBlankSet.delete(node as HTMLAnchorElement);
      }
    }
  });
}

const HTML: ForwardRefForwardPropsComponent<HTMLProps> = forwardRef(function HTML(
  { testId, content, customTags, ...restProps }: PropsWithoutRef<HTMLProps>,
  ref: HTMLProps['ref']
) {
  const [mounted, setMounted] = useState(false);
  const sanitizedHtml = useMemo(() => {
    // Don't render content on the server at all.
    return mounted && DOMPurify.isSupported
      ? DOMPurify.sanitize(content, {
          FORBID_TAGS: ['style', 'font'],
          CUSTOM_ELEMENT_HANDLING: {
            tagNameCheck: customTags ? tagName => customTags.includes(tagName) : undefined
          },
          ALLOWED_URI_REGEXP: allowedURI
        })
      : '';
  }, [content, customTags, mounted]);
  const testIds = useTestIds(testId, getHTMLTestIds);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <StyledHTML
      data-testid={testIds.root}
      {...restProps}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      ref={ref}
    />
  );
});

export default withTestIds(HTML, getHTMLTestIds);
